import React from 'react'
import flatpickr from 'flatpickr'
import styled from 'styled-components'
// import { Options } from 'flatpickr/dist/types/options'

import { FieldMetadata, FieldProps, useFieldMetadata } from '../FieldMetadata'
import FormInput, { FormInputProps } from './FormInput'
import { flatPickerStyles } from './flatpickr'
import { Flex } from '../Flex'
import { isSameDay } from 'date-fns'
import { CustomStyleOverrideProps } from './types'

const inputClassName = 'flatpickr-form-input'

type DatePickerProps = {
  inline?: boolean
  input?: boolean //if false, the input will not be rendered
  enable?: {
    to?: string | null
    from?: string | null
  }
  dateFormat?: 'F j, Y' | 'M d' | 'F j, Y H:i K'
  enableTime?: boolean
}

export type FormDateRangeInputProps = (Omit<FieldProps, 'size' | 'type'> &
  DatePickerProps &
  Omit<FormInputProps, 'onChange'>) & {
  onChange?: (val?: [string, string]) => void
  value?: [string, string] | null
}

const FormInputWrapper = styled.div<{
  disabled?: boolean
  baseOverrides?: Partial<CustomStyleOverrideProps>
}>`
  position: relative;

  width: 100%;

  &:first-child {
    border-right: 1px solid var(--gray200);
  }

  ${(props) => flatPickerStyles(props.baseOverrides)}
  
  .${inputClassName} {
    ${(props) =>
      props.disabled &&
      `
    background-color: var(--gray100);
    cursor: not-allowed;
    color: var(--gray600);
    `}
  }
`

const FormDateInput = ({
  inline,
  input,
  errorMessage,
  value,
  onChange,
  disabled,
  clearable,
  enable,
  leftAdornment,
  rightAdornment,
  dateFormat,
  enableTime,
  ...props
}: FormDateRangeInputProps) => {
  const { fieldProps, ...fieldMetadataProps } = useFieldMetadata({
    ...props,
    errorMessage,
  })
  const wrapperDivToRef = React.useRef<HTMLDivElement>(null)
  const wrapperDivFromRef = React.useRef<HTMLDivElement>(null)
  const inputFromRef = React.useRef<HTMLInputElement>(null)
  const inputToRef = React.useRef<HTMLInputElement>(null)
  const flatpickrToRef = React.useRef<flatpickr.Instance | null>(null)
  const flatpickrFromRef = React.useRef<flatpickr.Instance | null>(null)

  const [datePickerValue, setDatePickerValue] = React.useState<{
    dateStart: Date | null
    dateEnd: Date | null
  }>({
    dateStart: value && !!value[0] ? new Date(value[0]) : null,
    dateEnd: value && value[1] ? new Date(value[1]) : null,
  })

  React.useEffect(() => {
    if (value) {
      // setDatePickerValue({
      //   dateStart: !!value[0] ? new Date(value[0]) : null,
      //   dateEnd: !!value[1] ? new Date(value[1]) : null,
      // })
      // if (datePickerValue.dateStart && flatpickrFromRef.current) {
      //   flatpickrFromRef.current.setDate(datePickerValue.dateStart, false)
      // }
      // if (datePickerValue.dateEnd && flatpickrToRef.current) {
      //   flatpickrToRef.current.setDate(datePickerValue.dateEnd, false)
      // }
      // If value has changed, only update the value if it's different from the current value
      const [newDateStart, newDateEnd] = value
      if (!newDateStart && !newDateEnd) {
        if (!datePickerValue.dateStart && !datePickerValue.dateEnd) {
          return
        }

        setDatePickerValue({
          dateStart: null,
          dateEnd: null,
        })
        flatpickrFromRef.current?.clear()
        flatpickrToRef.current?.clear()
      }
      if (newDateStart !== datePickerValue.dateStart?.toISOString() || newDateEnd !== datePickerValue.dateEnd?.toISOString()) {
        const newDateStartDate = newDateStart ? new Date(newDateStart) : null
        const newDateEndDate = newDateEnd ? new Date(newDateEnd) : null
        setDatePickerValue({
          dateStart: newDateStartDate,
          dateEnd: newDateEndDate,
        })
        
        if (newDateStartDate && flatpickrFromRef.current) {
          flatpickrFromRef.current.setDate(newDateStartDate, false)
        }
        if (newDateEndDate && flatpickrToRef.current) {
          flatpickrToRef.current.setDate(newDateEndDate, false)
        }
      }
    }
  }, [value])

  React.useEffect(() => {
    if (flatpickrToRef.current) {
      flatpickrToRef.current.set('clickOpens', !disabled)
    }
    if (flatpickrFromRef.current) {
      flatpickrFromRef.current.set('clickOpens', !disabled)
    }
  }, [disabled, flatpickrToRef.current, flatpickrFromRef.current])

  const handleFromChange = React.useCallback(
    (selectedDates: Date[] | undefined) => {
      setDatePickerValue((prev) => {
        //if changing date to the same date, clear the input
        if (
          selectedDates?.[0] &&
          !!prev.dateStart &&
          isSameDay(prev.dateStart, selectedDates[0])
        ) {
          // istanbul ignore next
          flatpickrFromRef.current?.clear()

          return {
            ...prev,
            dateStart: null,
          }
        }

        return {
          ...prev,
          dateStart: selectedDates?.[0] ?? null,
        }
      })
    },
    [onChange]
  )
  const handleToChange = React.useCallback(
    (selectedDates: Date[] | undefined) => {
      setDatePickerValue((prev) => {
        //if changing date to the same date, clear the input
        if (
          selectedDates?.[0] &&
          !!prev.dateEnd &&
          isSameDay(prev.dateEnd, selectedDates?.[0])
        ) {
          // istanbul ignore next
          flatpickrToRef.current?.clear()

          return {
            ...prev,
            dateEnd: null,
          }
        }

        return {
          ...prev,
          dateEnd: selectedDates?.[0] ?? null,
        }
      })
    },
    [onChange]
  )

  React.useEffect(() => {
    const results: [string, string] = [
      datePickerValue.dateStart?.toISOString() ?? '',
      datePickerValue.dateEnd?.toISOString() ?? '',
    ]

    onChange?.(results)
  }, [datePickerValue])

  // React.useEffect(() => {
  //   if (flatpickrToRef.current) {
  //     flatpickrToRef.current.set('onChange', handleChange)
  //   }
  //   if (flatpickrFromRef.current) {
  //     flatpickrFromRef.current.set('onChange', handleChange)
  //   }
  // }, [handleChange, flatpickrToRef.current, flatpickrFromRef.current])

  React.useEffect(() => {
    if (!wrapperDivToRef.current || !wrapperDivFromRef.current) return
    if (!inputFromRef.current || !inputToRef.current) return

    const useRegularDateFormat = !enableTime && !dateFormat

    const datePickerProps: {
      mode: 'single' | 'range'
      inline?: boolean
      disableMobile: boolean
      altInput: boolean
      altFormat: string
      altInputClass: string
      dateFormat: string
      enableTime?: boolean
      defaultDate?: Date[]
      onReady: (a: any, b: any, fp: any) => void
    } = {
      mode: 'single',
      inline,
      disableMobile: true,
      altInput: true,
      altFormat:
        dateFormat ?? (useRegularDateFormat ? 'F j, Y' : 'F j, Y H:i K'),
      altInputClass: inputClassName,
      dateFormat: useRegularDateFormat ? 'Y-m-d H:i' : 'Y-m-d H:i:S',
      enableTime,
      onReady: (a, b, fp) => {
        fp.altInput!.setAttribute('aria-label', 'date picker input')
      },
    }

    flatpickrFromRef.current = flatpickr(inputFromRef.current, {
      ...datePickerProps,
      // onDayCreate: function (dObj, dStr, fp, dayElem) {
      //   dayElem.addEventListener('mouseover', function () {
      //     console.log('HOVER', {
      //       fp,
      //     })

      //     if (datePickerValue.dateStart) {
      //       console.log('datePickerValue.dateStart', datePickerValue.dateStart)
      //       console.log('dObj?.[0]', dObj?.[0])

      //       if (dObj[0] < datePickerValue.dateStart) {
      //         dayElem.classList.add('highlight')
      //       } else {
      //         dayElem.classList.remove('highlight')
      //       }
      //     }
      //   })
      // },
      defaultDate: !!datePickerValue.dateStart
        ? [datePickerValue.dateStart]
        : [],
      appendTo: wrapperDivFromRef.current as HTMLElement,
      onChange: (selectedDates) => {
        // istanbul ignore next
        flatpickrToRef.current?.set('minDate', selectedDates[0])

        handleFromChange(selectedDates)
      },
    })
    flatpickrToRef.current = flatpickr(inputToRef.current, {
      ...datePickerProps,
      defaultDate: !!datePickerValue.dateEnd ? [datePickerValue.dateEnd] : [],
      appendTo: wrapperDivToRef.current as HTMLElement,
      onChange: (selectedDates) => {
        //TODO: check is date is after to date, if so, set to date to selected date
        // flatpickrFromRef.current?.set('maxDate', selectedDates[0])
        handleToChange(selectedDates)
      },
    })

    if (!!enable && enable.to && enable.from) {
      // istanbul ignore next
      if (flatpickrToRef.current && flatpickrToRef.current.set) {
        // istanbul ignore next
        flatpickrToRef.current.set('enable', [enable as any])
        // istanbul ignore next
        flatpickrFromRef.current.set('enable', [enable as any])
      }
    }

    return () => {
      if (flatpickrToRef.current?.destroy) {
        flatpickrToRef.current?.destroy()
      }
      if (flatpickrFromRef.current?.destroy) {
        flatpickrFromRef.current?.destroy()
      }
    }
  }, [wrapperDivToRef, inputFromRef, inputToRef])

  return (
    <FieldMetadata
      {...fieldMetadataProps}
      required={props.required}
      aria-label='date input'
      style={{ width: '100%' }}
    >
      <Flex direction='row' gap='0'>
        <FormInputWrapper
          ref={wrapperDivFromRef}
          disabled={disabled}
          aria-label='date input'
          data-testid='form-date-start-input-testid'
          baseOverrides={props.baseOverrides}
        >
          <FormInput
            leftAdornment={leftAdornment}
            rightAdornment={rightAdornment}
            ref={inputFromRef}
            aria-label='date start form input'
            style={{
              ...props.style,
              display: inline && input === false ? 'none' : 'block',
            }}
            clearable={clearable}
          />
        </FormInputWrapper>
        <FormInputWrapper
          ref={wrapperDivToRef}
          disabled={disabled}
          aria-label='date input'
          data-testid='form-date-end-input-testid'
          baseOverrides={props.baseOverrides}
        >
          <FormInput
            leftAdornment={leftAdornment}
            rightAdornment={rightAdornment}
            ref={inputToRef}
            aria-label='date end form input'
            style={{
              ...props.style,
              display: inline && input === false ? 'none' : 'block',
            }}
            clearable={clearable}
          />
        </FormInputWrapper>
      </Flex>
    </FieldMetadata>
  )
}
export default FormDateInput
